






















import { CreateElement, VNode, VNodeDirective } from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { SwExtendedVue } from '../sw-extended-vue';
import { dropdown, SwDropdownUnit, SwDropdownUnitDivider, DropdownDirValues } from '../dropdown-dir';
import { DropMenuItem } from './types';
import { SwIcon, IconConfig } from '../sw-icon';
import { utilService } from '../util-service';

const cssPrefix = 'sw-drop-menu';
const i18nPathPrefix = 'libShared.SwDropMenu';

/**
 * @event request-close `(event: MouseEvent)`
 * @event select `(itemId: string)`
 */
@Component({
  directives: {
    dropdown,
  },
})
export default class SwDropMenu extends SwExtendedVue {
  @Prop({ type: Boolean, required: true })
  opened: boolean;

  @Prop({ type: Array, required: true })
  items: DropMenuItem[];

  @Prop({ type: String, default: 'left' })
  align: 'left' | 'right' | undefined;

  @Prop({ type: Boolean, default: false })
  withArrow: boolean;

  @Prop({ type: Number, default: 500 })
  maxWidth: number;

  @Prop({ type: Number, default: 1000 })
  maxHeight: number;

  cssPrefix = cssPrefix;
  i18nPathPrefix = i18nPathPrefix;
  openedIdPerLevel: string[] = [];

  get dropdownDirValues(): DropdownDirValues {
    return this.composeDropdown(this.items, this.opened, 0);
  }

  @Watch('opened')
  private onOpenedChange() {
    if (!this.opened) {
      this.openedIdPerLevel = [];
    }
  }

  private itemHasSubMenu(item: DropMenuItem) {
    return !!item.items && item.items.length > 0;
  }

  private cleanOpenedIdAfterLevel(level: number) {
    for (let i = level + 1; i < this.openedIdPerLevel.length; i += 1) {
      this.$set(this.openedIdPerLevel, i, '');
    }
  }

  private composeDropdown(items: DropMenuItem[], visible: boolean, menuLevel: number): DropdownDirValues {
    return {
      visible,
      placement: menuLevel > 0 ? 'right-start' : (this.align === 'left' ? 'bottom-start' : (this.align === 'right' ? 'bottom-end' : 'bottom')),
      trigger: 'controlled',
      withArrow: this.withArrow,
      maxWidth: this.maxWidth,
      maxHeight: this.maxHeight,
      content: (h: CreateElement): VNode[] => {// tslint:disable-line
        const nodes: VNode[] = [];
        items.forEach((item) => {
          if (item.divider === true) {
            nodes.push(
              <SwDropdownUnitDivider /> as VNode,
            );
            return;
          }

          const clickable = item.clickable !== undefined ? item.clickable : true;
          const directives: VNodeDirective[] = [];

          if (this.itemHasSubMenu(item)) {
            directives.push({
              name: 'dropdown',
              value: this.composeDropdown(item.items!, this.openedIdPerLevel[menuLevel] === item.id, menuLevel + 1),
            });
          }

          nodes.push(
            <SwDropdownUnit
              {...{ directives }}
              props-disabled={!!item.disabled}
              props-clickable={clickable}
              props-selected={this.openedIdPerLevel[menuLevel] === item.id}
              nativeOn-click={() => { this.onClickItem(item, menuLevel); }}
            >
              {
                item.icons && item.icons.length > 0 ? item.icons.map((icon) => {
                  const iconConfig = Object.assign(
                    {
                      size: 14,
                    } as IconConfig,
                    icon,
                  );
                  if (item.disabled) {
                    delete iconConfig.color;
                  }
                  return (
                    <SwIcon class={[`${cssPrefix}__icon`, 'sw-flexbox__flex-none']} {...{ props: iconConfig }}/>
                  );
                }) : null
              }
              <span class={['sw-flexbox__flex', 'sw-flexbox', 'sw-flexbox--center-items']}>
                { typeof item.content === 'function' ? item.content(h) : item.content }
              </span>
              {
                this.itemHasSubMenu(item) ? (() => {
                  const iconConfig: IconConfig = {
                    size: 10,
                    name: 'arrow-up',
                    rotate: 90,
                    color: this.openedIdPerLevel[menuLevel] === item.id ? utilService.colorConst.selected : undefined,
                  };
                  if (item.disabled) {
                    delete iconConfig.color;
                  }
                  return (
                    <SwIcon class={[`${cssPrefix}__sub-icon`, 'sw-flexbox__flex-none']} {...{ props: iconConfig }}/>
                  );
                })() : null
              }
            </SwDropdownUnit> as VNode,
          );
        });
        return nodes;
      },
      onClickOutsidePopper: (event: MouseEvent) => {
        if (menuLevel > 0) { return; }
        const targetEl = event.target as HTMLElement;
        const dropdownEl = targetEl.closest('.sw-dropdown');
        if (!!dropdownEl) { return; }
        this.$emit('request-close', event);
      },
    };
  }

  private onClickItem(item: DropMenuItem, level: number) {
    if (item.clickable === false) { return; }
    if (item.disabled === true) { return; }

    if (this.itemHasSubMenu(item)) {
      const idForLevel = this.openedIdPerLevel[level];

      this.$set(this.openedIdPerLevel, level, !idForLevel || idForLevel !== item.id ? item.id : '');
      this.cleanOpenedIdAfterLevel(level);
    } else {
      this.$set(this.openedIdPerLevel, level, '');
      this.cleanOpenedIdAfterLevel(level);
      this.$emit('select', item.id);
    }
  }
}
